/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Mar 16 2023
 **/

import React from "react";
import { colors } from "@/frameworks/core/styles";

import { css, Interpolation, Theme } from "@emotion/react";

interface AvatarStatusProps extends React.HtmlHTMLAttributes<HTMLDivElement> {
	status: "Red" | "Green" | "Gray" | "Yellow";
	size: "Small" | "Medium" | "Large";
}

type ColorAvatarProps = Omit<AvatarStatusProps, "status">;

/**
 * @param size 크기 ex) "Small" | "Medium" | "Large"
 * @param status: 종류 ex) "Red" | "Green" | "Gray" | "Yellow"
 */
export default function AvatarStatus({ size, status, ...props }: AvatarStatusProps) {
	if (status === "Red") return <RedAvatarStatus {...props} size={size} />;
	if (status === "Green") return <GreenAvatarStatus {...props} size={size} />;
	if (status === "Gray") return <GreyAvatarStatus {...props} size={size} />;
	if (status === "Yellow") return <YellowAvatarStatus {...props} size={size} />;
	return null;
}

function RedAvatarStatus({ size, ...props }: ColorAvatarProps) {
	return (
		<div {...props} css={[sizeCssMap[size], colorCss(colors.error_60_100)]}>
			<div css={redChildMap[size]}></div>
		</div>
	);
}

function GreenAvatarStatus({ size, ...props }: ColorAvatarProps) {
	return <div {...props} css={[sizeCssMap[size], colorCss(colors.success_60_100)]}></div>;
}

function GreyAvatarStatus({ size, ...props }: ColorAvatarProps) {
	return <div {...props} css={[sizeCssMap[size], colorCss(colors.neutral_60_100)]}></div>;
}

function YellowAvatarStatus({ size, ...props }: ColorAvatarProps) {
	return <div {...props} css={[sizeCssMap[size], colorCss("#FFB800")]}></div>;
}

const colorCss = (color: string) => css`
	background-color: ${color};
`;

const defaultCss = css`
	box-sizing: border-box;
	display: flex;
	justify-content: center;
	align-items: center;
`;

const smallSizeCss = css`
	${defaultCss}
	background-color: red;
	width: 8px;
	height: 8px;
	border-radius: 8px;
	border: 1px solid white;
`;

const mediumSizeCss = css`
	${defaultCss}
	background-color: red;
	width: 10px;
	height: 10px;
	border-radius: 10px;
	border: 1px solid white;
`;

const largeSizeCss = css`
	${defaultCss}
	width: 16px;
	height: 16px;
	border-radius: 16px;
	border: 1.3px solid white;
`;

const sizeCssMap: { [k in AvatarStatusProps["size"]]: Interpolation<Theme> } = {
	["Small"]: smallSizeCss,
	["Medium"]: mediumSizeCss,
	["Large"]: largeSizeCss
};

const redChildMap: { [k in AvatarStatusProps["size"]]: Interpolation<Theme> } = {
	["Small"]: css`
		background-color: white;
		width: 4px;
		height: 1px;
	`,
	["Medium"]: css`
		background-color: white;
		width: 6px;
		height: 1px;
	`,
	["Large"]: css`
		background-color: white;
		width: 6px;
		height: 2px;
	`
};
